/**
 * CORE-620 (актуализация 27.10.2026) — поиск карточки события в канале круга.
 *
 * `WatchChannel` отдаёт только новейшие 50 сообщений всего канала круга мастера,
 * а карточку эфира в живом круге быстро вытесняют другие: на DEV у 38 из 39
 * ближайших эфиров после карточки лежало от 69 до 477 сообщений. Обсуждение на
 * странице Live её не находило и всё равно показывало себя готовым.
 *
 * Порядок тот же, что у `EventThread` и обсуждений на дашборде (CORE-1683,
 * CORE-1685): по уже известному корню тред тянется одним запросом; иначе
 * история листается назад страницами, пока карточка не найдётся. Поиск
 * ограничен: исчерпали страницы — карточки в канале нет.
 *
 * Отдельным модулем, без React и контекстов: так его можно проверить на
 * подставном канале, а хук остаётся тонким.
 */

/** Предел страниц — тот же, что у `EventThread` и `DiscussionThreadPanel`. */
export const MAX_ROOT_SEARCH_PAGES = 30;

/** Что поиску нужно от канала — подмножество `ChatApiChannel`. */
export interface EventCardSearchChannel {
  /**
   * Следующая страница истории старше загруженной; `false` — старше нет.
   * Упавший запрос — исключение (возврат QA 30.10): по нему поиск и отличает
   * `failed` от `absent`.
   */
  loadOlder(): Promise<boolean>;
  /** Тред целиком по известному корню; сбой — исключение, как у `loadOlder`. */
  loadThread(rootId: string): Promise<boolean>;
}

/**
 * Найти карточку: `true` — она теперь среди загруженных сообщений канала.
 *
 * @param hasCard — есть ли карточка среди уже загруженного (проверяется после
 *   каждого шага, поэтому подхватывает и то, что пришло живым событием).
 * @param knownRootId — числовой id карточки, если она уже находилась раньше
 *   (после переподключения канал перечитывает новейшие 50, и она выпадает).
 * @param isCancelled — хук размонтирован или сменил эфир: дальше не листаем.
 */
export async function searchEventCard(
  src: EventCardSearchChannel,
  hasCard: () => boolean,
  knownRootId: string | null,
  isCancelled: () => boolean = () => false,
  maxPages: number = MAX_ROOT_SEARCH_PAGES,
): Promise<boolean> {
  if (hasCard()) return true;
  // Тред по корню не пришёл — история всё равно листается, как и раньше;
  // не нашлась карточка и там — это сбой, а не «карточки нет».
  let threadError: unknown = null;
  if (knownRootId) {
    try {
      await src.loadThread(knownRootId);
    } catch (err) {
      threadError = err;
    }
    if (isCancelled()) return false;
    if (hasCard()) return true;
  }
  for (let i = 0; i < maxPages && !isCancelled(); i++) {
    const got = await src.loadOlder();
    if (hasCard()) return true;
    if (!got) break;
  }
  if (threadError) throw threadError;
  return false;
}

/**
 * CORE-620, возврат QA 28.10 — какая карточка канала относится к этому событию.
 *
 * Канал круга общий для всех событий мастера, а `custom.eventId` — это id из
 * своей таблицы у каждого вида: у эфира 1856 и у комнаты 1856 карточки разные,
 * а число одно. Сравнение одного `eventId` нашло на `/live/1856` карточку
 * комнаты, и ответ ушёл в её обсуждение. Пока окно было в 50 сообщений, до неё
 * просто не доставали; глубокий поиск дошёл.
 *
 * Поэтому:
 * - есть `discussionId` из ответа эфира — карточка ищется только по нему: это
 *   её точный внешний id, по нему её адресует сам бэкенд;
 * - нет — запасной путь по `eventId`, и только вместе с совпавшим `eventKind`.
 */
export interface EventCardTarget {
  eventId: number | null;
  eventKind: string;
  /** Внешний id карточки из ответа события; `undefined` — экран его не знает. */
  discussionId?: string | null;
}

/** Сообщение канала в той мере, в какой оно нужно для сверки. */
export interface EventCardCandidate {
  raw?: unknown;
}

export function eventCardMatcher(target: EventCardTarget): (m: EventCardCandidate) => boolean {
  const { discussionId, eventId, eventKind } = target;
  if (typeof discussionId === 'string' && discussionId !== '') {
    return (m) => {
      const raw = m.raw as Record<string, unknown> | undefined;
      const ext = raw?.externalStreamId ?? raw?.ExternalStreamId;
      return ext != null && String(ext) === discussionId;
    };
  }
  return (m) => {
    if (eventId == null) return false;
    const custom = ((m.raw as Record<string, any> | undefined)?.custom ?? {}) as Record<string, unknown>;
    const kind = custom.eventKind ?? custom.event_kind;
    if (kind !== eventKind) return false;
    const rawId = custom.eventId ?? custom.event_id;
    if (rawId === undefined || rawId === null) return false;
    const n = typeof rawId === 'number' ? rawId : Number(rawId);
    return Number.isFinite(n) && n === eventId;
  };
}

/**
 * Чем закончился поиск: `found` — карточка среди загруженного; `absent` —
 * история пройдена честно, карточки нет; `failed` — поиск оборвался (сеть,
 * сервер), про карточку ничего не известно.
 *
 * `failed` и `absent` нельзя склеивать: первое повторяют, второе — нет. Одно
 * упавшее листание раньше показывало «обсуждения нет» навсегда.
 */
export type EventCardSearchOutcome = 'found' | 'absent' | 'failed';

export async function locateEventCard(
  src: EventCardSearchChannel,
  hasCard: () => boolean,
  knownRootId: string | null,
  isCancelled: () => boolean = () => false,
  maxPages: number = MAX_ROOT_SEARCH_PAGES,
): Promise<EventCardSearchOutcome> {
  try {
    return (await searchEventCard(src, hasCard, knownRootId, isCancelled, maxPages)) ? 'found' : 'absent';
  } catch {
    return 'failed';
  }
}
